import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import * as flags from 'country-flag-icons/react/3x2'
import { statusLabelMap } from '../studentStatus'
import { studentsFiltersStore } from '../../../model/studentsFiltersStore'
import { ROUTES } from '../../../../../shared/config/routes'
import {
	getStudentCountriesDisplay,
	getStudentCountryShortLabel,
} from '../../../constants/studentCountriesDisplay'
import s from '../styles/TableView.module.scss'

const capitalize = value =>
	value ? value.charAt(0).toUpperCase() + value.slice(1) : ''

export const useStudentsDataTableColumns = ({ isMobile, onToggleOne }) => {
	const navigate = useNavigate()
	const activeColumns = studentsFiltersStore.activeColumns

	const colsMeta = useMemo(() => {
		return [
			{ id: 'select' },
			{
				id: 'student',
				label: 'Student',
				sortableKey: 'student',
				width: isMobile ? '120px' : '250px',
			},
			{
				id: 'grade',
				label: 'Grade',
				sortableKey: 'grade',
				width: isMobile ? '90px' : '120px',
				getValue: r => r?.grade,
			},
			{
				id: 'country',
				label: 'Country',
				sortableKey: 'country',
				width: isMobile ? '140px' : '240px',
			},
			{
				id: 'course',
				label: 'Course',
				sortableKey: 'course',
				width: isMobile ? '180px' : '220px',
				getValue: r => r?.course,
			},
			{
				id: 'counselor',
				label: 'Counselor',
				sortableKey: 'counselor',
				width: isMobile ? '150px' : '190px',
				getValue: r => r?.counselor,
			},
			{
				id: 'deadline',
				label: 'Upcoming deadline',
				sortableKey: 'nearest_deadline',
				width: isMobile ? '120px' : '160px',
				getValue: r => r?.nearest_deadline || r?.nearest_deadline_display,
			},
		]
	}, [isMobile])

	const currentCol = useMemo(() => {
		return (activeColumns || [])
			.map(c => {
				const meta = colsMeta.find(el => el.id === c?.id)
				if (!meta) return null
				return { ...meta }
			})
			.filter(Boolean)
	}, [colsMeta, activeColumns])

	const getSortOptions = useCallback(key => {
		if (!key) return []

		const numericKeys = new Set([
			'grade',
			'missingDocuments',
			'documentsMissing',
			'students',
			'studentsCount',
		])
		const dateKeys = new Set([
			'nearest_deadline',
			'deadline',
			'joined_date',
			'due_date',
		])

		if (numericKeys.has(key)) {
			return [
				{ id: `${key}_asc`, key, dir: 'asc', label: 'Low → High' },
				{ id: `${key}_desc`, key, dir: 'desc', label: 'High → Low' },
			]
		}
		if (dateKeys.has(key)) {
			return [
				{ id: `${key}_asc`, key, dir: 'asc', label: 'Earliest first' },
				{ id: `${key}_desc`, key, dir: 'desc', label: 'Latest first' },
			]
		}

		return [
			{ id: `${key}_asc`, key, dir: 'asc', label: 'A → Z' },
			{ id: `${key}_desc`, key, dir: 'desc', label: 'Z → A' },
		]
	}, [])

	const getRowClickHandler = useCallback(() => {
		return row => {
			if (row?.id) {
				const normalizedId = String(row.id).trim()
				const encodedId = encodeURIComponent(normalizedId)
				navigate(`${ROUTES.STUDENT_PROFILE}/${encodedId}`)
			}
		}
	}, [navigate])

	const getCountriesMeta = useCallback(row => {
		const { items, moreCount } = getStudentCountriesDisplay(row)
		return {
			items: items.map(item => ({
				code: item.code,
				label:
					item.label === 'NE'
						? 'NZ'
						: item.label || getStudentCountryShortLabel(item.code),
				FlagIcon: item.code ? flags[item.code] : null,
			})),
			moreCount,
		}
	}, [])

	const getStatusMeta = useCallback(row => {
		const statusKey = row?.status
		const statusLabel =
			statusLabelMap?.[statusKey] || row?.tag || statusKey || '—'
		const statusClass = s[`tag${capitalize(statusKey)}`] || ''
		return { statusLabel, statusClass }
	}, [])

	return {
		currentCol,
		getSortOptions,
		getRowClickHandler,
		getCountriesMeta,
		getStatusMeta,
	}
}
